import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../Styles/LeaveRequest.css";
import Loader from "../../Components/Loader"; // Assuming you have a Loader component

function AllLeaves() {
  const [allLeaves, setAllLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // Loader state
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [leavesPerPage] = useState(10);

  const calculateTotalDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDifference = end - start;
    const daysDifference = timeDifference / (1000 * 3600 * 24);
    return daysDifference + 1; // Include start date
  };

  const fetchAllLeaves = async () => {
    setLoading(true);
    setError(null); // Clear any previous errors

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found. Please log in.");
        throw new Error("No authentication token found");
      }

      let facultyLeaves = [];
      let studentLeaves = [];

      // Try to fetch faculty leaves
      try {
        const facultyResponse = await axios.get(
          "https://api.uniums.com/api/faculty/all-leaves",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        facultyLeaves = (facultyResponse.data.data || []).map((leave) => ({
          ...leave,
          userType: "Faculty",
        }));
      } catch (facultyErr) {
        console.error("Error fetching faculty leaves:", facultyErr);
        // Instead of setting error state, we'll just continue with empty faculty leaves
      }

      // Try to fetch student leaves
      try {
        const studentResponse = await axios.get(
          "https://api.uniums.com/api/student/all-leaves-student",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        studentLeaves = (studentResponse.data.data || []).map((leave) => ({
          ...leave,
          userType: "Student",
        }));
      } catch (studentErr) {
        console.error("Error fetching student leaves:", studentErr);
        // Instead of setting error state, we'll just continue with empty student leaves
      }

      const combinedLeaves = [...facultyLeaves, ...studentLeaves];

      if (combinedLeaves.length === 0) {
        setError("No leave requests found from either faculty or students.");
      }

      setAllLeaves(combinedLeaves);
      setFilteredLeaves(combinedLeaves);
    } catch (err) {
      console.error("Error fetching all leave requests:", err);
      setError("Failed to fetch leave requests. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query) {
      const filtered = allLeaves.filter(
        (request) =>
          request.employeeCode?.toLowerCase().includes(query.toLowerCase()) ||
          request.name?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredLeaves(filtered);
    } else {
      setFilteredLeaves(allLeaves);
    }
  };

  const indexOfLastLeave = currentPage * leavesPerPage;
  const indexOfFirstLeave = indexOfLastLeave - leavesPerPage;
  const currentLeaves = filteredLeaves.slice(
    indexOfFirstLeave,
    indexOfLastLeave
  );
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (date) => {
    const newDate = new Date(date);
    return newDate.toLocaleDateString("en-GB");
  };

  useEffect(() => {
    fetchAllLeaves();
  }, []);

  if (loading) {
    return <Loader />; // Display loader while fetching data
  }

  return (
    <div className="leave-management">
      <h1>All Leave Requests</h1>
      {/* {error && <p className="error-message">{error}</p>} */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by Employee Code or Name"
          value={searchQuery}
          onChange={handleSearch}
          className="search-box"
        />
      </div>
      <div className="admin-table-container border_top">
        <div className="faculty-table">
          <table>
            <thead>
              <tr className="header_sticky">
                <th className="text_nowrap">SL No</th>
                <th className="text_nowrap">Name</th>
                <th className="text_nowrap">Status</th>
                <th className="text_nowrap">User Type</th>
                <th className="min_width_200">Region</th>
                <th className="text_nowrap">Leave Type</th>
                <th className="text_nowrap">From Date</th>
                <th className="text_nowrap">To Date</th>
                <th className="text_nowrap">Total Days</th>
                <th className="min_width_200">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {currentLeaves.length > 0 ? (
                currentLeaves.map((request, index) => (
                  <tr key={request._id}>
                    <td className="text_nowrap">{index + 1 + indexOfFirstLeave}</td>
                    <td className="text_nowrap">{request.name || "N/A"}</td>
                    <td className="text_nowrap">{request.status || "Pending"}</td>
                    <td className="text_nowrap">{request.userType}</td>
                    <td>{request.reason || "N/A"}</td>
                    <td className="text_nowrap">{request.leaveType || "N/A"}</td>
                    <td>{formatDate(request.startDate) || "N/A"}</td>
                    <td>{formatDate(request.endDate) || "N/A"}</td>
                    <td className="text_nowrap">
                      {calculateTotalDays(request.startDate, request.endDate)}
                    </td>
                    <td>{request.adminComment || "N/A"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="error">
                    No leave requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination all_leaves">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className="Prev_btn"
        >
          Prev
        </button>
        <span>Page {currentPage}</span>
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={
            currentPage === Math.ceil(filteredLeaves.length / leavesPerPage)
          }
          className="next_btn"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default AllLeaves;
